/**
 * ============================================================================
 * COUPON SERVICE - INTEGRAÇÃO COM SUPABASE
 * ============================================================================
 * 
 * Serviço para validação de cupons de desconto consultando a tabela
 * 'coupons' do Supabase. Integra 100% com o painel administrativo.
 */

import { supabase } from '../lib/supabase';

export interface Coupon {
  id: string;
  code: string;
  discount_type: 'percentage' | 'fixed';
  discount_value: number;
  min_purchase?: number;
  max_discount?: number;
  usage_limit?: number;
  usage_count: number;
  valid_from: string;
  valid_until: string;
  active: boolean;
  first_purchase_only?: boolean;
  created_at: string;
}

export interface CouponValidationResult {
  valid: boolean;
  coupon?: Coupon;
  discount?: number;
  error?: string;
}

/**
 * Valida um cupom consultando o banco de dados
 * @param code - Código do cupom
 * @param cartTotal - Total do carrinho (para validar min_purchase)
 * @param userEmail - Email do usuário (para validar cupom de primeira compra)
 * @returns Resultado da validação com desconto calculado
 */
export const validateCoupon = async (
  code: string,
  cartTotal: number,
  userEmail?: string
): Promise<CouponValidationResult> => {
  try {
    // Buscar cupom no banco
    const { data: coupon, error } = await supabase
      .from('coupons')
      .select('*')
      .eq('code', code.toUpperCase())
      .single();

    if (error || !coupon) {
      return {
        valid: false,
        error: 'Cupom não encontrado'
      };
    }

    // Verificar se está ativo
    if (!coupon.active) {
      return {
        valid: false,
        error: 'Cupom inativo'
      };
    }

    // Verificar data de validade
    const now = new Date();
    const validFrom = new Date(coupon.valid_from || coupon.start_date);
    const validUntil = new Date(coupon.valid_until || coupon.end_date);

    if (now < validFrom) {
      return {
        valid: false,
        error: 'Cupom ainda não está válido'
      };
    }

    if (now > validUntil) {
      return {
        valid: false,
        error: 'Cupom expirado'
      };
    }

    // Verificar limite de uso
    const usageLimit = coupon.usage_limit ?? coupon.max_uses;
    const usageCount = coupon.usage_count ?? coupon.used_count ?? 0;
    if (usageLimit && usageCount >= usageLimit) {
      return {
        valid: false,
        error: 'Limite de uso do cupom atingido'
      };
    }

    // Verificar compra mínima
    const minPurchase = coupon.min_purchase ?? coupon.minimum_order ?? 0;
    if (minPurchase && cartTotal < minPurchase) {
      return {
        valid: false,
        error: `Compra mínima de CHF ${Number(minPurchase).toFixed(2)} necessária`
      };
    }

    // Verificar se é cupom de primeira compra
    if (coupon.first_purchase_only) {
      if (!userEmail) {
        return {
          valid: false,
          error: 'Faça login para usar este cupom de boas-vindas'
        };
      }
      const { data: hasPrior } = await supabase.rpc('has_prior_orders', { p_email: userEmail });
      if (hasPrior) {
        return {
          valid: false,
          error: 'Este cupom é válido apenas para a primeira compra'
        };
      }
    }

    // Calcular desconto
    const discountType = coupon.discount_type ?? coupon.type ?? 'percentage';
    const discountValue = coupon.discount_value ?? coupon.value ?? 0;
    let discount = 0;
    if (discountType === 'percentage') {
      discount = (cartTotal * discountValue) / 100;
      const maxDiscount = coupon.max_discount;
      if (maxDiscount && discount > maxDiscount) {
        discount = maxDiscount;
      }
    } else {
      discount = discountValue;
    }

    return {
      valid: true,
      coupon,
      discount
    };
  } catch (error) {
    console.error('❌ Erro ao validar cupom:', error);
    return {
      valid: false,
      error: 'Erro ao validar cupom'
    };
  }
};

/**
 * Incrementa o contador de uso do cupom
 * @param couponId - ID do cupom
 */
export const incrementCouponUsage = async (couponId: string): Promise<void> => {
  try {
    const { error } = await supabase.rpc('increment_coupon_usage', {
      coupon_id: couponId
    });

    if (error) {
      console.error('❌ Erro ao incrementar uso do cupom:', error);
    }
  } catch (error) {
    console.error('❌ Erro ao incrementar uso do cupom:', error);
  }
};

/**
 * Lista cupons ativos e válidos
 */
export const getActiveCoupons = async (): Promise<Coupon[]> => {
  try {
    const now = new Date().toISOString();
    
    const { data, error } = await supabase
      .from('coupons')
      .select('*')
      .eq('active', true)
      .lte('valid_from', now)
      .gte('valid_until', now)
      .order('discount_value', { ascending: false });

    if (error) {
      console.error('❌ Erro ao buscar cupons:', error);
      return [];
    }

    return data || [];
  } catch (error) {
    console.error('❌ Erro ao buscar cupons:', error);
    return [];
  }
};
